import { Box, Card, CardContent, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import { Delete as DeleteIcon } from "@mui/icons-material";
import DocCardImage from "../DocCardImage/DocCardImage";
import { useTranslation } from 'react-i18next';

const FileDropZone = ({ setParentData, maxFiles = 1 }) => {
  const { t } = useTranslation();
  const [alert, setAlert] = useState("");
  const [formData, setFormData] = useState({ files: [] });

  useEffect(() => {
    if (alert !== "") {
      Swal.fire({ icon: "warning", text: alert, confirmButtonText: "Okay" });
    }
  }, [alert]);

  const ACCEPT = {
    "application/pdf": [],
    "application/msword": [],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
    "application/vnd.ms-excel": [],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
    "text/csv": [],
    "image/png": [],
    "image/jpeg": [],
    "image/jpg": [],
  };

  const onDropAccepted = (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    const incoming = acceptedFiles;
    let next = [...(formData.files || []), ...incoming];
    if (maxFiles && next.length > maxFiles) {
      next = next.slice(0, maxFiles);
      Swal.fire({ icon: "info", text: `${t('GENERAL.FILES_ALLOWED').replace('file(s)', maxFiles + ' file(s)')}`, confirmButtonText: "Okay" });
    }
    setFormData((prev) => ({ ...prev, files: next }));
    if (setParentData) setParentData((prev) => ({ ...prev, files: next }));
    setAlert("");
  };

  const onDropRejected = (fileRejections) => {
    const names = (fileRejections || []).map((r) => r.file?.name).filter(Boolean).join(", ");
    const msg = names
      ? `${t('GENERAL.UNSUPPORTED_FILE_TYPES')} ${names}. ${t('GENERAL.PLEASE_UPLOAD_IMAGE_PDF_WORD_EXCEL_OR_CSV_FILES')}`
      : `${t('GENERAL.UNSUPPORTED_FILE_TYPE_PLEASE_UPLOAD_IMAGE_PDF_WORD_EXCEL_OR_CSV_FILES')}`;
    Swal.fire({ icon: "warning", text: msg, confirmButtonText: "Okay" });
    setAlert(msg);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPT,
    multiple: maxFiles !== 1,
    onDropAccepted,
    onDropRejected,
    useFsAccessApi: false,
  });

  const handleDeleteFile = (indexToDelete) => {
    const updatedFiles = formData.files.filter((_, index) => index !== indexToDelete);
    setFormData((prev) => ({ ...prev, files: updatedFiles }));
    if (setParentData) setParentData((prev) => ({ ...prev, files: updatedFiles }));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        {...getRootProps()}
        sx={{
          border: "2px dotted #ccc",
          borderRadius: 1,
          padding: 2,
          textAlign: "center",
          cursor: "pointer",
          width: "100%",
          minHeight: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isDragActive ? "#f0f0f0" : "transparent",
          "&:hover": {
            border: (theme) => `2px dotted ${theme.palette.primary.main}`,
            color: (theme) => theme.palette.primary.main,
          },
        }}
      >
        <input
          {...getInputProps({
            onClick: (e) => {
              e.target.value = null;
            },
          })}
        />
        <Typography sx={{ "&:hover": { color: (theme) => theme.palette.primary.main } }}>
          {formData.files && formData.files.length > 0
            ? t('GENERAL.CLICK_TO_ADD_MORE_FILES_OR_DRAG_AND_DROP_HERE')
            : t('GENERAL.DRAG_AND_DROP_FILES_HERE_OR_CLICK_TO_SELECT')}
        </Typography>
      </Box>
      {formData.files && formData.files.length > 0 && (
        <Box sx={{ width: "100%", mt: 2 }}>
          <Typography variant="body2" color="textSecondary" mb={1}>
            {t('GENERAL.ATTACHED_FILES')} ({formData.files.length})
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {formData.files.map((file, index) => (
              <Card key={index} variant="outlined" sx={{ width: "100%", "&:hover": { backgroundColor: "action.hover" } }}>
                <CardContent
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.5,
                    "&:last-child": { pb: 1.5 },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                    <DocCardImage type={file.type} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">{(file.size / 1024).toFixed(1)} KB</Typography>
                    </Box>
                    <Tooltip title={file.type}>
                      <Chip label={file.type || "Unknown"} size="small" variant="outlined" sx={{ ml: 1, mr: 1, maxWidth: 120, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} />
                    </Tooltip>
                  </Box>
                  <IconButton onClick={() => handleDeleteFile(index)} size="small" color="error" sx={{ ml: 1 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FileDropZone;

